import { z } from 'zod';
// ---------------------------------------------------------------------------
// VCL generation (Domain 7 — Document & PDF Engine, issue #10 atom 4)
// ---------------------------------------------------------------------------
/** `HH:MM`, the shape the cockpit stores and `PatientVisitDto` carries. */
const VisitTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
/**
 * Body of `POST /api/v1/documents/vcl/generate` (REQ-DOC-001…016).
 *
 * ONE identifier — the patient visit. Everything else (chart header,
 * enrollment numbers, trial, per-visit layout, open tasks) is resolved by the
 * service through the exported read doors of domains 2/3/4 — the same
 * single-id contract as `VclHeaderService` (atom 3b). Payload-first decision
 * (Q-DOC-008 carry-forward): the endpoint returns the composed JSON
 * document; the PDF transport is a separate atom.
 */
export const GenerateVclRequestSchema = z.object({
    patientVisitId: z.number().int().positive(),
});
/**
 * The protocol window of the visit label (REQ-DOC-010) — structured data, not
 * a preformatted string: the A4 template (atom 6) decides how to render it.
 * `referralVisitNumber` is resolved from the catalogue, so the template never
 * touches ids.
 */
export const VclVisitWindowSchema = z.object({
    enabled: z.boolean(),
    minDays: z.number().int().nullable(),
    optDays: z.number().int().nullable(),
    maxDays: z.number().int().nullable(),
    referralVisitNumber: z.number().int().nullable(),
});
/**
 * The A4 header (REQ-DOC-009…012). PESEL and phones travel MASKED by the
 * service (REQ-DOC-022/023 — the full values never enter the document
 * payload); name, surname and the patient numbers stay in clear for
 * operational identification. Both numbers travel as a pair — screening
 * leads, randomization is optional (Q-PAT-001).
 */
export const VclHeaderSchema = z.object({
    firstName: z.string().min(1).max(255),
    lastName: z.string().min(1).max(255),
    screeningNumber: z.string().min(1).max(64),
    randomizationNumber: z.string().max(64).nullable(),
    maskedPesel: z.string().nullable(),
    /** Every chart phone, masked, in registration order; the template prints the first. */
    maskedPhones: z.array(z.string()),
    birthDate: z.iso.date().nullable(),
    /** Age at `visitDate`; null when either date is missing. Never negative. */
    ageAtVisit: z.number().int().min(0).nullable(),
    visitNumber: z.number().int().positive(),
    visitName1: z.string().min(1).max(255),
    visitName2: z.string().max(255).nullable(),
    visitWindow: VclVisitWindowSchema,
    /** `dateReal ?? dateScheduled` — performed wins over booked (REQ-VIS-044). */
    visitDate: z.iso.date().nullable(),
    visitTime: VisitTimeSchema.nullable(),
    /** Polish lowercase weekday name ("poniedziałek"); null when there is no visit date. */
    dayOfWeek: z.string().nullable(),
    protocolNumber: z.string().min(1),
    siteNumber: z.string().min(1),
});
/** One printed line of the check-list: a sub-group selected by the visit layout (REQ-DOC-003). */
export const VclCheckListItemSchema = z.object({
    subGroupId: z.number().int().positive(),
    name: z.string().min(1).max(255),
    /**
     * Effective performer role `COALESCE(sub_group.role, main_group.role)`
     * (REQ-DOC-005, Q-DOC-002). Special roles ("pacjent"/"out") travel through
     * untouched — the template classifies (Q-DOC-003).
     */
    role: z.string().min(1).max(50),
    /** True when the role came from the parent group because the sub-group has none. */
    roleInherited: z.boolean(),
    /** Order of the sub-group inside this visit layout (REQ-DOC-004). */
    position: z.number().int(),
});
/** One capital-letter heading of the check-list with its lines (REQ-DOC-001/002). */
export const VclCheckListSectionSchema = z.object({
    mainGroupId: z.number().int().positive(),
    name: z.string().min(1).max(255),
    role: z.string().min(1).max(50),
    /** Order of the heading on the printout (REQ-DOC-002). */
    position: z.number().int(),
    items: z.array(VclCheckListItemSchema),
});
/** One OPEN TASKS entry, read from the Cockpit domain (REQ-DOC-015). */
export const VclOpenTaskSchema = z.object({
    taskId: z.number().int().positive(),
    name: z.string().min(1),
    comments: z.string().nullable(),
    startDate: z.iso.date(),
});
/**
 * The composed VCL document returned by the generation endpoint — header
 * (atom 3b) + check-list sections + open tasks (atom 3) merged into one
 * payload, everything the A4 template (atom 6) renders. The paper notes area
 * (REQ-DOC-016) carries no data — the template reserves the space.
 *
 * Sub-shapes are reachable through indexed access (`VclDocumentDto['header']`
 * …); dedicated sub-types are deliberately NOT exported so the internal
 * service interfaces (atoms 3/3b) stay the only `VclHeader`/`VclOpenTask`
 * names inside the API.
 */
export const VclDocumentDtoSchema = z.object({
    patientVisitId: z.number().int().positive(),
    header: VclHeaderSchema,
    /** A heading with no selected line never appears; [] = a visit without layout rows. */
    sections: z.array(VclCheckListSectionSchema),
    /** Empty when the patient has no open task; the template still reserves the section. */
    openTasks: z.array(VclOpenTaskSchema),
});
//# sourceMappingURL=vcl.schema.js.map